import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button } from 'react-bootstrap';
import { IconStar, IconStarFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import EmptyState from '../../components/feedback/EmptyState';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { usePermissions } from '../../shared/permissions';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';
import WarehouseCreateModal from './components/WarehouseCreateModal';
import TableSearchBar from './components/TableSearchBar';
import PriorityToggleButton from './components/PriorityToggleButton';
import PriorityReorderTable from './components/PriorityReorderTable';
import { useDebouncedSearch } from './hooks/useDebouncedSearch';
import { usePriorityReorder } from './hooks/usePriorityReorder';

const INITIAL_NEW_WAREHOUSE = { name: '', location: '', description: '', supplier: '' };

function Warehouses() {
  const { t } = useTranslation();
  const { canManage } = usePermissions();
  const queryClient = useQueryClient();
  const [showNewWarehouseModal, setShowNewWarehouseModal] = useState(false);
  const [newWarehouse, setNewWarehouse] = useState(INITIAL_NEW_WAREHOUSE);
  const [activeTab, setActiveTab] = useState('all');
  const { searchInput, search, handleSearchInputChange, handleSearchSubmit } = useDebouncedSearch();

  const { data: warehouses = [], isLoading, error } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => {
      const response = await api.get('warehouses/api/warehouses/list_all/', { raw: true });
      return response.warehouses || [];
    },
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get('warehouses/api/suppliers/?ordering=name'),
  });

  const createWarehouseMutation = useMutation({
    mutationFn: (warehouseData) =>
      api.post('warehouses/api/warehouses/bulk_create/', { warehouses: [{ ...warehouseData, supplier: warehouseData.supplier || null }] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      setShowNewWarehouseModal(false);
      setNewWarehouse(INITIAL_NEW_WAREHOUSE);
    },
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: ({ id, is_favorite }) => api.patch(`warehouses/api/warehouses/${id}/`, { is_favorite }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['warehouses'] }),
  });

  useRealtimeEvents((e) => {
    if (e.action === 'bulk_update') return;
    if (e.model === 'warehouses.stock' || e.model === 'warehouses.stockitem' || e.model === 'warehouses.product') {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
    }
  });

  const handleCreateWarehouse = (e) => {
    e.preventDefault();
    createWarehouseMutation.mutate(newWarehouse);
  };

  const handleFieldChange = (field, value) => {
    setNewWarehouse((current) => ({ ...current, [field]: value }));
  };

  const ownCount = useMemo(() => warehouses.filter((w) => !w.is_external).length, [warehouses]);
  const externalCount = useMemo(() => warehouses.filter((w) => w.is_external).length, [warehouses]);

  const filteredWarehouses = useMemo(() => {
    let list = warehouses;
    if (activeTab === 'own') list = list.filter((w) => !w.is_external);
    else if (activeTab === 'external') list = list.filter((w) => w.is_external);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((w) =>
        w.name?.toLowerCase().includes(q)
        || w.location?.toLowerCase().includes(q)
        || w.description?.toLowerCase().includes(q));
    }
    return list;
  }, [warehouses, activeTab, search]);

  const { sortMode, savingOrder, sortItems, enterSortMode, exitSortMode, handleDragEnd } = usePriorityReorder({
    fetchSortItems: async () => filteredWarehouses,
    saveOrder: (withUpdatedPriorities) => api.post('warehouses/api/warehouses/bulk_update_priority/', {
      warehouse_priorities: withUpdatedPriorities.map((warehouse) => ({ id: warehouse.id, priority: warehouse.priority })),
    }),
    onExit: () => queryClient.invalidateQueries({ queryKey: ['warehouses'] }),
  });

  return (
    <PageShell
      pretitle={t('warehouses.overview')}
      title={t('warehouses.title')}
      description={t('warehouses.subtitle', 'Overview of warehouse locations, stock breadth and inventory footprint across the business.')}
      actions={canManage ? (
        <Button variant="primary" onClick={() => setShowNewWarehouseModal(true)}>
          <i className="ti ti-plus me-2"></i>
          {t('warehouses.newWarehouse')}
        </Button>
      ) : null}
    >
      <PageSection bodyClassName="p-0">
        <div className="px-3 pt-3">
          <div className="d-flex flex-column flex-md-row gap-2 mb-3">
            <TableSearchBar
              value={searchInput}
              onChange={handleSearchInputChange}
              onSubmit={handleSearchSubmit}
              placeholder={t('warehouses.searchPlaceholder', 'Hledat sklad podle názvu nebo lokace…')}
              submitLabel={t('warehouses.searchButton', 'Hledat')}
            />
            {canManage && warehouses.length > 1 && (
              <PriorityToggleButton
                active={sortMode}
                saving={savingOrder}
                onClick={sortMode ? exitSortMode : enterSortMode}
                activeLabel={t('warehouses.reorderDone', 'Hotovo')}
                inactiveLabel={t('warehouses.reorderStart', 'Změnit pořadí')}
              />
            )}
          </div>

          <ul className="nav nav-pills mb-3">
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
                {t('warehouses.tabs.all', 'Všechny sklady')}
                <span className="badge bg-secondary-lt ms-2">{warehouses.length}</span>
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'own' ? 'active' : ''}`} onClick={() => setActiveTab('own')}>
                {t('warehouses.tabs.own', 'Vlastní sklad')}
                <span className="badge bg-secondary-lt ms-2">{ownCount}</span>
              </button>
            </li>
            <li className="nav-item">
              <button type="button" className={`nav-link ${activeTab === 'external' ? 'active' : ''}`} onClick={() => setActiveTab('external')}>
                {t('warehouses.tabs.external', 'Externí sklad')}
                <span className="badge bg-secondary-lt ms-2">{externalCount}</span>
              </button>
            </li>
          </ul>
        </div>

        <div className="px-3 pb-3">
          {isLoading ? (
            <CenteredLoader />
          ) : error ? (
            <Alert variant="danger" className="app-inline-alert">
              {t('warehouses.loadError', 'Failed to load warehouses. Please try again later.')}
            </Alert>
          ) : warehouses.length === 0 ? (
            <EmptyState
              icon={<i className="ti ti-building-warehouse app-empty-state__icon-glyph" aria-hidden="true" />}
              title={t('warehouses.title')}
              message={t('warehouses.emptyMessage', 'No warehouses found. Create the first warehouse to start managing stock locations.')}
              action={canManage ? (
                <Button variant="primary" onClick={() => setShowNewWarehouseModal(true)}>
                  <i className="ti ti-plus me-2"></i>
                  {t('warehouses.newWarehouse')}
                </Button>
              ) : null}
            />
          ) : sortMode ? (
            <PriorityReorderTable
              items={sortItems}
              droppableId="warehouses-sort"
              onDragEnd={handleDragEnd}
              saving={savingOrder}
              hint={t('warehouses.reorderHint', 'Přetažením změňte pořadí zobrazení skladů.')}
              columns={[
                { key: 'name', label: t('warehouses.name', 'Název'), className: 'fw-semibold', render: (w) => w.name },
                {
                  key: 'type',
                  label: t('warehouses.type', 'Typ'),
                  render: (w) => (w.is_external
                    ? <span className="badge bg-orange-lt text-orange">{t('warehouses.external', 'Externí')}</span>
                    : <span className="badge bg-blue-lt text-blue">{t('warehouses.own', 'Vlastní')}</span>),
                },
                { key: 'location', label: t('warehouses.location', 'Lokace'), render: (w) => w.location || '-' },
              ]}
            />
          ) : filteredWarehouses.length === 0 ? (
            <div className="text-center text-secondary py-4">
              {t('warehouses.noResults', 'Žádný sklad neodpovídá zvolenému filtru.')}
            </div>
          ) : (
            <div className="row g-4">
              {filteredWarehouses.map((stock) => (
                <div className="col-12 col-xl-6" key={stock.id}>
                  <PageSection
                    title={(
                      <>
                        <button
                          type="button"
                          className="btn btn-icon p-0 me-2 border-0 bg-transparent"
                          style={{ lineHeight: 1, verticalAlign: '-2px' }}
                          onClick={() => toggleFavoriteMutation.mutate({ id: stock.id, is_favorite: !stock.is_favorite })}
                          title={stock.is_favorite ? t('warehouses.unfavorite', 'Odebrat z oblíbených') : t('warehouses.favorite', 'Přidat k oblíbeným')}
                        >
                          {stock.is_favorite
                            ? <IconStarFilled size={18} className="text-warning" />
                            : <IconStar size={18} className="text-muted" />}
                        </button>
                        {stock.name}
                        {stock.is_external && (
                          <span className="badge bg-orange-lt text-orange ms-2">
                            {t('warehouses.external', 'Externí')}{stock.supplier_name ? ` — ${stock.supplier_name}` : ''}
                          </span>
                        )}
                      </>
                    )}
                    description={stock.location || t('warehouses.locationMissing', 'Location not specified yet.')}
                    actions={
                      <Link to={`/warehouses/stock/${stock.id}`} className="btn btn-primary">
                        {t('warehouses.details')}
                      </Link>
                    }
                    className="h-100"
                  >
                    <div className="datagrid">
                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('warehouses.totalItemsInWarehouse')}</div>
                        <div className="datagrid-content">{stock.items_total_count || 0}</div>
                      </div>

                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('warehouses.totalUniqueItemsInWarehouse')}</div>
                        <div className="datagrid-content">{stock.items_unique_count || 0}</div>
                      </div>

                      <div className="datagrid-item">
                        <div className="datagrid-title">{t('warehouses.totalItemsOfSameType')}</div>
                        <div className="datagrid-content">{stock.items_type_count || 0}</div>
                      </div>
                    </div>
                  </PageSection>
                </div>
              ))}
            </div>
          )}
        </div>
      </PageSection>

      <WarehouseCreateModal
        show={showNewWarehouseModal}
        formData={newWarehouse}
        submitting={createWarehouseMutation.isPending}
        suppliers={suppliers}
        onClose={() => setShowNewWarehouseModal(false)}
        onSubmit={handleCreateWarehouse}
        onFieldChange={handleFieldChange}
      />
    </PageShell>
  );
}

export default Warehouses;
